import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { faker } from "@faker-js/faker";

import { useTheme } from "@/hooks/use-theme";
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

// Mock data generators
const generateTimeSeriesData = (points: number) => {
  const now = new Date();

  return Array.from({ length: points }, (_, i) => ({
    timestamp: new Date(now.getTime() - (points - i) * 3600000).toISOString(),
    value: faker.number.int({ min: 10, max: 100 }),
  }));
};

const generateErrorDistribution = () => [
  { errorType: "Timeout", count: faker.number.int({ min: 5, max: 20 }) },
  { errorType: "API Error", count: faker.number.int({ min: 3, max: 15 }) },
  { errorType: "Validation", count: faker.number.int({ min: 1, max: 10 }) },
  { errorType: "Permission", count: faker.number.int({ min: 1, max: 8 }) },
  { errorType: "Other", count: faker.number.int({ min: 1, max: 5 }) },
];

const getMockMetrics = (timeRange: string) => ({
  summaryMetrics: {
    successRate: faker.number.float({ min: 95, max: 99.9 }),
    averageDuration: faker.number.float({ min: 0.5, max: 3 }),
    totalExecutions: faker.number.int({ min: 1000, max: 10000 }),
    errorRate: faker.number.float({ min: 0.1, max: 5 }),
    timeRange,
  },
  detailedMetrics: {
    executionsOverTime: generateTimeSeriesData(
      timeRange === "24h" ? 24 : timeRange === "7d" ? 28 : 30,
    ),
    errorDistribution: generateErrorDistribution(),
  },
});

export const WorkflowMetricsPage: React.FC = () => {
  const { workflowId } = useParams();
  const [timeRange, setTimeRange] = useState("24h");
  const { isDark } = useTheme();
  const { summaryMetrics, detailedMetrics } = getMockMetrics(timeRange);

  const getChartColors = () => {
    if (isDark) {
      return {
        text: "#ffffff",
        grid: "rgba(255, 255, 255, 0.1)",
        background: "rgba(255, 255, 255, 0.05)",
        border: "rgba(255, 255, 255, 0.1)",
      };
    }

    return {
      text: "#000000",
      grid: "rgba(0, 0, 0, 0.1)",
      background: "rgba(255, 255, 255, 0.5)",
      border: "rgba(0, 0, 0, 0.1)",
    };
  };

  const colors = getChartColors();

  const executionsData = {
    labels: detailedMetrics.executionsOverTime.map((d) =>
      new Date(d.timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    ),
    datasets: [
      {
        label: "Executions",
        data: detailedMetrics.executionsOverTime.map((d) => d.value),
        borderColor: isDark ? "rgb(100, 200, 255)" : "rgb(53, 162, 235)",
        backgroundColor: isDark ? "rgba(100, 200, 255, 0.2)" : "rgba(53, 162, 235, 0.5)",
        tension: 0.1,
      },
    ],
  };

  const errorData = {
    labels: detailedMetrics.errorDistribution.map((d) => d.errorType),
    datasets: [
      {
        label: "Errors",
        data: detailedMetrics.errorDistribution.map((d) => d.count),
        borderColor: isDark ? "rgb(255, 150, 150)" : "rgb(255, 99, 132)",
        backgroundColor: isDark ? "rgba(255, 150, 150, 0.2)" : "rgba(255, 99, 132, 0.5)",
        tension: 0.1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top" as const,
        labels: {
          color: colors.text,
        },
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          color: colors.grid,
        },
        ticks: {
          color: colors.text,
        },
      },
      y: {
        grid: {
          color: colors.grid,
        },
        ticks: {
          color: colors.text,
        },
      },
    },
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Workflow Metrics: {workflowId}</h1>
        <div className="flex gap-4">
          <select
            className="border rounded p-2"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          positive={true}
          title="Success Rate"
          trend="+0.5%"
          value={`${summaryMetrics.successRate.toFixed(1)}%`}
        />
        <MetricCard
          positive={true}
          title="Avg. Duration"
          trend="-0.1s"
          value={`${summaryMetrics.averageDuration.toFixed(2)}s`}
        />
        <MetricCard
          positive={true}
          title="Total Executions"
          trend="+2.3%"
          value={summaryMetrics.totalExecutions.toLocaleString()}
        />
        <MetricCard
          positive={true}
          title="Error Rate"
          trend="-0.5%"
          value={`${summaryMetrics.errorRate.toFixed(1)}%`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Executions Over Time">
          <Line data={executionsData} options={chartOptions} />
        </ChartCard>

        <ChartCard title="Error Distribution">
          <Line data={errorData} options={chartOptions} />
        </ChartCard>
      </div>
    </div>
  );
};

interface MetricCardProps {
  title: string;
  value: string;
  trend: string;
  positive: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, trend, positive }) => (
  <div className="rounded-lg shadow p-4">
    <h3 className="text-primary text-sm">{title}</h3>
    <div className="flex items-end gap-2">
      <span className="text-2xl font-bold">{value}</span>
      <span className={`text-sm ${positive ? "text-green-500" : "text-red-500"}`}>{trend}</span>
    </div>
  </div>
);

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-lg shadow p-4">
    <h2 className="text-lg font-semibold mb-4">{title}</h2>
    {children}
  </div>
);
